const BASE = process.env.REACT_APP_DEVICES_API_BASE || 'http://localhost:5004';

export const ENDPOINTS = {
  // Overview
  summary:           `${BASE}/devices/summary`,
  globalDataUsage:   `${BASE}/devices/data-usage`,
  apps:              `${BASE}/devices/apps`,
  grades:            `${BASE}/devices/grades`,
  appsByGrade:       (grade) => `${BASE}/devices/apps/grade/${encodeURIComponent(grade)}`,
  globalMap:         (bounds) => bounds
    ? `${BASE}/devices/map?swLat=${bounds.swLat}&swLng=${bounds.swLng}&neLat=${bounds.neLat}&neLng=${bounds.neLng}`
    : `${BASE}/devices/map`,

  // District
  district:          (name) => `${BASE}/devices/region/${encodeURIComponent(name)}`,
  districtDevices:   (name, page = 1, limit = 20, search = '') => `${BASE}/devices/region/${encodeURIComponent(name)}/paginated?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
  districtDevicesExport: (name, search = '') => `${BASE}/devices/region/${encodeURIComponent(name)}/export${search ? `?search=${encodeURIComponent(search)}` : ''}`,
  districtBySchool:  (name, page = 1, limit = 100) => `${BASE}/devices/region/${encodeURIComponent(name)}/by-school?page=${page}&limit=${limit}`,
  districtDataUsage: (name) => `${BASE}/devices/data-usage/district/${encodeURIComponent(name)}`,
  districtMap:       (name) => `${BASE}/devices/map/district/${encodeURIComponent(name)}`,

  // Circuit
  circuit:           (name) => `${BASE}/devices/summary/circuit/${encodeURIComponent(name)}`,
  circuitDevices:    (name, page = 1, limit = 20, search = '') => `${BASE}/devices/circuit/${encodeURIComponent(name)}?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
  circuitDevicesExport: (name, search = '') => `${BASE}/devices/circuit/${encodeURIComponent(name)}/export${search ? `?search=${encodeURIComponent(search)}` : ''}`,
  circuitByGrade:    (name) => `${BASE}/devices/circuit/${encodeURIComponent(name)}/by-grade`,
  circuitDataUsage:  (name) => `${BASE}/devices/data-usage/circuit/${encodeURIComponent(name)}`,
  circuitMap:        (name) => `${BASE}/devices/map/circuit/${encodeURIComponent(name)}`,

  // School
  school:            (name) => `${BASE}/devices/school/${encodeURIComponent(name)}/summary`,
  schoolDevices:     (name, page = 1, limit = 20, search = '') => `${BASE}/devices/school/${encodeURIComponent(name)}?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
  schoolByGrade:     (name) => `${BASE}/devices/apps/school/${encodeURIComponent(name)}`,
  schoolMap:         (name) => `${BASE}/devices/map/school/${encodeURIComponent(name)}`,

  // Device
  device:            (imei) => `${BASE}/devices/imei/${imei}`,
  reportIssue:            `${BASE}/issues`,
  lostStolenByDistrict:   `${BASE}/issues/lost-stolen-by-district`,
  deviceIssues:      (imei) => `${BASE}/issues/device/${encodeURIComponent(imei)}`,
  resolveIssue:      (id) => `${BASE}/issues/${id}/resolve`,

  // Status counts (total / online / offline)
  status:            (scope) => {
    if (!scope) return `${BASE}/devices/status`;
    const [key, val] = Object.entries(scope)[0];
    return `${BASE}/devices/status?${key}=${encodeURIComponent(val)}`;
  },
};

const CARRIER_META = {
  'mtn-sa':  { label: 'MTN',     color: '#F5C518' },
  'vodacom': { label: 'Vodacom', color: '#E62801' },
  'others':  { label: 'Other',   color: '#505050' },
};


// [{ _id: 'mtn-sa', count: N }] → [{ name, value, pct, color }]
export function normalizeCarriers(raw = []) {
  const ORDER = ['mtn-sa', 'vodacom', 'others'];
  const total = raw.reduce((s, c) => s + (c.count || 0), 0) || 1;
  const map = Object.fromEntries(raw.filter((c) => c._id).map((c) => [c._id, c.count || 0]));
  return ORDER
    .filter((id) => map[id] !== undefined)
    .map((id) => {
      const count = map[id];
      const meta = CARRIER_META[id] || { label: id, color: '#505050' };
      return { name: meta.label, value: count, pct: Math.round((count / total) * 100), color: meta.color };
    });
}

// { "MOPANI EAST": 450 } → [{ name, count }] sorted desc
export function normalizeNameCountMap(obj = {}) {
  return Object.entries(obj)
    .map(([name, count]) => ({ name: toTitleCase(name), count }))
    .sort((a, b) => b.count - a.count);
}

// [{ _id: 'mtn-sa'|'vodacom'|'others', totalBytes: N }] → [{ name, value (GiB), color }]
export function normalizeDataUsage(raw = []) {
  const ORDER = ['mtn-sa', 'vodacom', 'others'];
  const map = Object.fromEntries(raw.filter((d) => d._id).map((d) => [d._id, d]));
  return ORDER
    .filter((id) => map[id])
    .map((id) => {
      const { totalBytes = 0 } = map[id];
      const meta = CARRIER_META[id] || { label: id, color: '#505050' };
      return {
        name:  meta.label,
        value: parseFloat((totalBytes / 1073741824).toFixed(1)),
        color: meta.color,
      };
    });
}

function toTitleCase(str = '') {
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatDateTime(v) {
  if (!v) return null;
  const d = new Date(v);
  const day   = d.getDate();
  const month = d.toLocaleString('en-ZA', { month: 'short' });
  const year  = d.getFullYear();
  const h     = d.getHours();
  const min   = String(d.getMinutes()).padStart(2, '0');
  const ampm  = h >= 12 ? 'pm' : 'am';
  const hour  = h % 12 || 12;
  return `${day} ${month} ${year} ${hour}:${min} ${ampm}`;
}

export function timeAgo(v) {
  if (!v) return '—';
  const s = Math.floor((Date.now() - new Date(v).getTime()) / 1000);
  if (s < 60)           return `${s} second${s !== 1 ? 's' : ''} ago`;
  const m = Math.floor(s / 60);
  if (m < 60)           return `${m} minute${m !== 1 ? 's' : ''} ago`;
  const h = Math.floor(m / 60);
  if (h < 24)           return `${h} hour${h !== 1 ? 's' : ''} ago`;
  const d = Math.floor(h / 24);
  if (d < 30)           return `${d} day${d !== 1 ? 's' : ''} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12)          return `${mo} month${mo !== 1 ? 's' : ''} ago`;
  const y = Math.floor(d / 365);
  return `${y} year${y !== 1 ? 's' : ''} ago`;
}
